import { produce } from 'immer';
import moment from 'moment-timezone';
import { convertToMap, snakeToCamelCase } from '.';
import { RootState } from '../app/store';
import { MenuStages } from '../constants/enums';
import { CartModifierGroup } from './cart';
import {
  Category,
  CategoryAndTimePeriodQueryQuery,
  MenuItem,
  MenuItemSetting,
  MenuItemsQueryQuery,
  MenuOverride,
  ModalityType,
  ModifierGroup,
  ModifierGroupsQueryQuery,
  OverrideKey,
  PosProperties,
  RestaurantInfoQuery,
  SecondaryType,
  TimePeriod,
} from '../generated-interfaces/graphql';
import { RestaurantSettings } from '../reducers/restaurantSlice';
import {
  IBrandMenuCacheEntry,
  IBrandMenusOrderData,
  IMenuData,
  IMenusOrderData,
} from '../redux/features/cachedMenu/cachedMenu.props';
import {
  addBrandMenuData,
  addMenuData,
  cacheBrandMenuUtterances,
  setBrandMenu,
  setBrandMenuUtterances,
  setBrandMenuVersionForRestaurant,
  updateBrandMenuData,
  updateMenuData,
} from '../redux/features/cachedMenu/cachedMenu.slice';
import { IFetchUnavailableItem, IUnavailableItem } from '../types';
import { memoize } from './cache';
import { CartItem, getCartItemNonDummyNode, getPosPropertyValue } from './cart';
import { MOD_SEPARATOR, VOICE_PROPERTIES } from './constants';
import logger from './logger';
import { GATEWAY_TIMEOUT_MS } from './retry';
import {
  ExpandName,
  generateModSymbolMapping,
  ModSymbolCodeNameMappingType,
} from './mappings';
import {
  getGraphQLClient,
  getMenuFromMenuAPI,
  getMenuURLFromMenuUnificationMenuAPI,
  getMenuURLFromMenuAPI,
  getPersistentMenuPropByRestaurant,
} from './network';
import { GenericMap, Override } from './types';
import { IComboItemMatch } from '../reducers/menuSlice';
import {
  A_LA_CARTE_POS_ID,
  DUMMY_ITEM,
  POS_ID,
  POS_NAME,
  AUTO_COMBOS,
  FULL_WEEKDAY_FORMAT,
} from '../constants';
import {
  IBrandMenu,
  IRestaurantMealPeriod,
  IRestaurantUnavailableEntity,
  IUtterance,
} from '../types/brandMenu';
import { ParsedMenuItem, ParsedModifierGroup } from '../types/menu';
import { getOptionModifierGroups } from './lazyOptionBuild';

type AlwaysAvailableTimePeriod = TimePeriod;
export const alwaysAvailableTimePeriod: AlwaysAvailableTimePeriod = {
  id: '-1',
  startDate: null,
  endDate: null,
  description: null,
  availability: [0, 1, 2, 3, 4, 5, 6].map((day) => {
    return {
      day,
      hours: [],
      alwaysEnabled: true,
    };
  }),
  timePeriodCategoryMappings: [],
};

export function formatMenuResponse(menuRes: MenuItemsQueryQuery) {
  return {
    menuItems: convertToMap(menuRes.menuItems),
    overrides: convertToMap(menuRes.menuOverrides),
    menuItemSettings: convertToMap(menuRes.menuItemSettings),
    posSettings: convertToMap(menuRes.posProperties),
  } as {
    menuItems: GenericMap<MenuItem>;
    overrides: GenericMap<MenuOverride>;
    menuItemSettings: GenericMap<MenuItemSetting>;
    posSettings: GenericMap<PosProperties>;
  };
}

export function formatCategoryAndTimePeriodResponse(
  categoryAndTimePeriodRes: CategoryAndTimePeriodQueryQuery
) {
  return {
    categories: convertToMap(categoryAndTimePeriodRes.categories),
    timePeriods: convertToMap(categoryAndTimePeriodRes.timePeriods),
  } as {
    categories: GenericMap<Category>;
    timePeriods: GenericMap<TimePeriod>;
  };
}

export function formatModGroupResponse(
  modifierGroupRes: ModifierGroupsQueryQuery
) {
  return {
    modifierGroups: convertToMap(modifierGroupRes.modifierGroups),
  } as {
    modifierGroups: GenericMap<ModifierGroup>;
  };
}

// "Re-Flatten" menu items but AFTER setting the category and children and stuff for easier use later
export function flattenMenuItems(
  menuItems: GenericMap<ParsedMenuItem>
): GenericMap<ParsedMenuItem> {
  return Object.values(menuItems).reduce((acc, item) => {
    if (!acc[item.itemId]) {
      acc[item.itemId] = item;
      for (let childModGroup of Object.values(item.modifierGroups)) {
        if (!flattenedModGroups[childModGroup.id]) {
          flattenedModGroups[childModGroup.id] = childModGroup;
          Object.assign(acc, flattenMenuItems(childModGroup.menuItems));
        }
      }
    }
    return acc;
  }, {} as { [itemId: string]: ParsedMenuItem });
}

/*
 * if
 * A -> A .. childModifer is excluded.
 *
 * if
 * A -> B -> A
 * ... We get in a loop.
 *
 * How do we prevent the HITL from choking?
 * (Without exploding into 100,000,000 menu items)
 *
 * So... We only go down one level!
 */
function parseROPModifierGroup(
  modifierGroupId: string,
  menuRes: MenuResponses,
  persistentVoiceProps: GenericMap<PersistentMenuProperty>,
  unavailableItems: Record<string, IUnavailableItem>,
  stage: string,
  history: MenuItem[] = []
): ParsedModifierGroup {
  if (parsedModifierGroups[modifierGroupId]) {
    return parsedModifierGroups[modifierGroupId];
  }

  const modifierGroup = menuRes.modifierGroups[modifierGroupId];
  const {
    displayName: modGroupDisplayName,
    description: modGroupDescription,
    isUpsell,
    isUpsellPrompt,
    label,
  } = getVoiceProps(modifierGroup.name, persistentVoiceProps);
  const itemsToAdd = modifierGroup.menuItems
    .filter(
      (menuItemId) =>
        !history.map((menuItem) => menuItem.id).includes('' + menuItemId)
    )
    .map((menuItemId) => ({
      ...parseROPMenuItem(
        String(menuItemId),
        modifierGroup.id,
        menuRes,
        persistentVoiceProps,
        unavailableItems,
        stage,
        history
      ),
      parentModifierGroupName: modifierGroup.name,
      parentModifierGroupDisplayName: modGroupDisplayName,
      parentModifierGroupId: modifierGroup.id,
    }))
    .filter((item) => item.available || isItem86edToday(item));

  const result = {
    ...modifierGroup,
    name: modGroupDisplayName ? modGroupDisplayName : modifierGroup.name,
    description: modGroupDescription
      ? modGroupDescription
      : modifierGroup.description,
    menuItems: convertToMap(itemsToAdd),
    prpName: modifierGroup.name,
    ...(isUpsell !== undefined && { isUpsell }),
    ...(isUpsellPrompt !== undefined && { isUpsellPrompt }),
    ...(label && { label }),
  };
  parsedModifierGroups[modifierGroupId] = result;
  return result;
}

const parsedMenuItems: GenericMap<ParsedMenuItem> = {}; // key should be combination of menu item id + modifier group id/category id
const parsedModifierGroups: GenericMap<ParsedModifierGroup> = {};
const flattenedModGroups: GenericMap<ParsedModifierGroup> = {};

export interface MenuResponses {
  menuItems: GenericMap<MenuItem>;
  overrides: GenericMap<MenuOverride>;
  menuItemSettings: GenericMap<MenuItemSetting>;
  posSettings: GenericMap<PosProperties>;
  categories: GenericMap<Category>;
  timePeriods: GenericMap<TimePeriod>;
  modifierGroups: GenericMap<ModifierGroup>;
}

export const getPosProperties = (
  menuItem: MenuItem,
  menuRes: MenuResponses
) => {
  return (
    menuItem?.posProperties?.reduce((pps, id) => {
      if (menuRes?.posSettings[id]) pps[id] = menuRes.posSettings[id];
      return pps;
    }, {} as GenericMap<PosProperties>) || {}
  );
};

export function buildFullMenuItem(
  item: TopLevelMenuItem,
  menuRes: MenuResponses,
  persistentVoiceProps: GenericMap<PersistentMenuProperty>,
  unavailableItems: Record<string, IUnavailableItem>,
  stage: string
): ParsedMenuItem {
  const menuItem = menuRes.menuItems[item.id];
  const categoryId = item.categoryId;
  const itemId = item.id + '-' + categoryId;
  const filteredCategory = menuRes.categories[categoryId].sortOrder.filter(
    (order) => order.id === Number(item.id)
  );

  const {
    displayName: menuItemDisplayName,
    description: menuItemDescription,
    isUpsell,
    isUpsellPrompt,
    label,
  } = getVoiceProps(menuItem.name, persistentVoiceProps);

  const modifierGroups = menuItem.modifierGroups.map((modifierGroupId) =>
    parseROPModifierGroup(
      String(modifierGroupId),
      menuRes,
      persistentVoiceProps,
      unavailableItems,
      stage
    )
  );

  const modifierGroupsMap = convertToMap(modifierGroups);

  const posProperties = getPosProperties(menuItem, menuRes);

  const result = {
    ...menuItem,
    name: ExpandName(menuItemDisplayName ? menuItemDisplayName : menuItem.name),
    description: menuItemDescription
      ? menuItemDescription
      : menuItem.description,
    category: item.category,
    categoryId: item.categoryId,
    itemId,
    originalMenuItemId: String(item.id),
    containsOwnModifierGroup: false,
    // Dinero library requires the price in integer cents
    price: Math.round(menuItem.price * 100),
    imageUrl: menuItem.imageUrl ?? '/no_menu_image.jpg',
    sortOrderByCategory:
      filteredCategory.length > 0 ? filteredCategory[0].sortOrder : null,
    sortOrderByModifierGroup: null,
    modifierGroups: modifierGroupsMap,
    overrides: convertToMap(
      menuItem.menuOverrides
        .map((id) => menuRes.overrides[id])
        .filter((override) => {
          return (
            override.secondaryType === null ||
            (override.secondaryType === SecondaryType.Category &&
              override.secondaryId === categoryId)
          );
        })
    ),
    menuItemSettings: convertToMap(
      menuItem.menuItemSettings
        .map((id) => menuRes.menuItemSettings[id])
        .filter((setting) => {
          return String(setting.menuItemId) === menuItem.id;
        })
    ),
    posProperties,
    ...(isUpsell !== undefined && { isUpsell }),
    ...(isUpsellPrompt !== undefined && { isUpsellPrompt }),
    ...(label && { label }),
  };
  // use itemId as the unique id to store parsed menu items
  parsedMenuItems[itemId] = result;
  return result;
}

function parseROPMenuItem(
  menuItemId: string,
  parentModifierGroupId: string,
  menuRes: MenuResponses,
  persistentVoiceProps: GenericMap<PersistentMenuProperty>,
  unavailableItems: Record<string, IUnavailableItem>,
  stage: string,
  history: MenuItem[] = []
): ParsedMenuItem {
  const itemId = menuItemId + '-' + parentModifierGroupId;
  if (parsedMenuItems[itemId]) {
    return parsedMenuItems[itemId];
  }

  const menuItem: MenuItem = menuRes.menuItems[menuItemId];
  const { available, unavailableUntil } = getUnAvailableUntilAndAvailable({
    item: menuItem,
    stage,
    unavailableItems,
  });
  const parentModifierGroup = menuRes.modifierGroups[parentModifierGroupId];
  const {
    displayName: menuItemDisplayName,
    description: menuItemDescription,
    isUpsell,
    isUpsellPrompt,
    label,
    synonyms,
  } = getVoiceProps(menuItem.name, persistentVoiceProps);

  const result = {
    ...menuItem,
    name: ExpandName(menuItemDisplayName ? menuItemDisplayName : menuItem.name),
    description: menuItemDescription
      ? menuItemDescription
      : menuItem.description,
    category: 'modifier',
    categoryId: null,
    containsOwnModifierGroup: false,
    itemId: menuItemId,
    originalMenuItemId: menuItemId,
    // Dinero library requires the price in integer cents
    price: Math.round(menuItem.price * 100),
    imageUrl: menuItem.imageUrl ?? '/no_menu_image.jpg',
    sortOrderByCategory: null,
    sortOrderByModifierGroup:
      parentModifierGroup.sortOrder.filter(
        (order) => String(order.id) === menuItemId
      ).length > 0
        ? parentModifierGroup.sortOrder.filter(
            (order) => String(order.id) === menuItemId
          )[0].sortOrder
        : null,
    modifierGroups: {},
    overrides: convertToMap(
      menuItem.menuOverrides
        .map((id) => menuRes.overrides[id])
        .filter((override) => {
          return (
            override.secondaryType === null ||
            (override.secondaryType === SecondaryType.ModifierGroup &&
              override.secondaryId === parentModifierGroupId)
          );
        })
    ),
    menuItemSettings: convertToMap(
      menuItem.menuItemSettings
        .map((id) => menuRes.menuItemSettings[id])
        .filter((setting) => {
          return String(setting.menuItemId) === menuItem.id;
        })
    ),
    posProperties: convertToMap(
      Object.values(menuRes.posSettings).filter(
        (setting) =>
          setting.objectPrimaryKey === menuItem.id &&
          setting.propertyType === 'MENU_ITEM'
      )
    ),
    ...(isUpsell !== undefined && { isUpsell }),
    ...(isUpsellPrompt !== undefined && { isUpsellPrompt }),
    ...(label && { label }),
    synonyms: synonyms || '',
    available,
    unavailableUntil,
  };

  if (history.length < 10) {
    // use itemId as the unique id to store parsed menu items
    parsedMenuItems[itemId] = result;
    // Recurse after
    result.containsOwnModifierGroup = menuItem.modifierGroups.includes(
      parseInt(parentModifierGroupId)
    );
    result.modifierGroups = convertToMap(
      menuItem.modifierGroups
        .filter(
          (modifierGroupId) => String(modifierGroupId) !== parentModifierGroupId
        )
        .map((modifierGroupId) =>
          parseROPModifierGroup(
            String(modifierGroupId),
            menuRes,
            persistentVoiceProps,
            unavailableItems,
            stage,
            history.concat(menuItem)
          )
        )
    );
  }
  return result;
}

export type ParsedCategory = Override<
  Category,
  {
    timePeriods: TimePeriod[];
    activeTimePeriod?: ActiveTimePeriod;
    itemNames?: string[];
    inactive?: boolean;
  }
>;

export function parseCategoryAndTimeperiodResponse(
  categoryAndTimePeriodRes: ReturnType<
    typeof formatCategoryAndTimePeriodResponse
  >
) {
  const categoriesWithTimePeriod: ParsedCategory[] = [];
  const alwaysAvailableCategoryWithTimePeriods: ParsedCategory[] = [];
  Object.values(categoryAndTimePeriodRes.categories)
    .sort((a, b) => {
      // sort categories by ownSortOrder
      if (a.ownSortOrder !== null && b.ownSortOrder !== null) {
        return a.ownSortOrder - b.ownSortOrder;
      } else if (a.ownSortOrder !== null) {
        return -1;
      } else if (b.ownSortOrder !== null) {
        return 1;
      }
      return 0;
    })
    .forEach((category) => {
      if (!category.timePeriods.length) {
        alwaysAvailableCategoryWithTimePeriods.push({
          ...category,
          timePeriods: [alwaysAvailableTimePeriod],
        });
      } else {
        categoriesWithTimePeriod.push({
          ...category,
          timePeriods: category.timePeriods.map(
            (id) => categoryAndTimePeriodRes.timePeriods[id]
          ),
        });
      }
    });
  const autoComboCategory = [
    ...categoriesWithTimePeriod,
    ...alwaysAvailableCategoryWithTimePeriods,
  ].find((category) => category.name === AUTO_COMBOS);

  const autoComboPrpIds =
    autoComboCategory && autoComboCategory.menuItems
      ? autoComboCategory.menuItems
      : [];
  let sortedAutoComboPrpIds;
  if (autoComboCategory?.sortOrder) {
    const autoComboCategorySortOrder = convertToMap(
      autoComboCategory?.sortOrder
    );
    sortedAutoComboPrpIds =
      autoComboPrpIds
        .slice()
        .sort((a, b) => {
          if (autoComboCategorySortOrder[a] && autoComboCategorySortOrder[b]) {
            const aSortOrder = autoComboCategorySortOrder[a].sortOrder;
            const bSortOrder = autoComboCategorySortOrder[b].sortOrder;
            if (aSortOrder !== null && bSortOrder !== null) {
              return aSortOrder - bSortOrder;
            }
          } else if (autoComboCategorySortOrder[a]) {
            return -1;
          } else if (autoComboCategorySortOrder[b]) {
            return 1;
          }
          return 0;
        })
        .map((id) => String(id)) || [];
  }

  return {
    categoriesWithTimePeriod,
    alwaysAvailableCategoryWithTimePeriods,
    autoComboPrpIds: sortedAutoComboPrpIds
      ? sortedAutoComboPrpIds
      : autoComboPrpIds.map((id) => String(id)),
  };
}

export type TopLevelMenuItem = {
  category: string;
  categoryId: string;
  id: string;
  name: string;
  available: boolean;
  unavailableUntil?: string | null;
  modcode?: string;
  speak?: string;
  synonyms?: string | string[];
  description?: string;
  activeTimePeriod?: ActiveTimePeriod;
  inactive?: boolean;
  pathToItem?: string;
  parentModifierGroupName?: string;
  parentModifierGroupDisplayName?: string;
  parentModifierGroupId?: string;
  slashCommands?: string[];
  slashCommandIdx?: number;
  // The slash-command item's constraint active for this meal period and restaurant group.
  activeConstraintName?: string;
  readbackName?: string;
  // HITL-1570: true when the constraint carries the `speak_total_in_upsell`
  // condition — the prompt speaks the check total, so the donation upsell is
  // skipped while no total is available and speaking it lets Complete skip the
  // total readback. Only set on slash-command (tts-prompt/upsell) items.
  speakTotalInUpsell?: boolean;
};

// The active top-level item a queued reminder command's intent names.
export const findTopLevelItemByIntent = (
  topLevelMenuItems: Record<string, TopLevelMenuItem>,
  intentName: string
) =>
  Object.values(topLevelMenuItems).find(
    (item) =>
      !item.inactive &&
      (item.name === intentName || item.name.startsWith(intentName))
  );

export function parseMenuResponse({
  categories,
  menuRes,
  persistentVoiceProps,
  unavailableItems,
  stage,
}: {
  categories: ParsedCategory[];
  menuRes: ReturnType<typeof formatMenuResponse>;
  persistentVoiceProps: GenericMap<PersistentMenuProperty>;
  unavailableItems: Record<string, IUnavailableItem>;
  stage: string;
}) {
  let modSymbolMapping: ModSymbolCodeNameMappingType = {};
  let codeNameMapping: ModSymbolCodeNameMappingType = {};

  categories.sort((a, b) => {
    // sort categories by ownSortOrder
    if (a.ownSortOrder !== null && b.ownSortOrder !== null) {
      return a.ownSortOrder - b.ownSortOrder;
    } else if (a.ownSortOrder !== null) {
      return -1;
    } else if (b.ownSortOrder !== null) {
      return 1;
    }
    return 0;
  });

  const topLevelMenuItems = categories.reduce((acc, category) => {
    category.menuItems
      .filter((id) => {
        if (!menuRes.menuItems[id]) {
          return false;
        }
        const { available, unavailableUntil } = getUnAvailableUntilAndAvailable(
          { item: menuRes.menuItems[id], stage, unavailableItems }
        );
        const is86edIndefinitely = isItem86edIndefinitely(
          available,
          unavailableUntil
        );
        return !menuRes.menuItems[id].isModifierOnly && !is86edIndefinitely;
      })
      .forEach((menuItemId) => {
        const {
          name: categoryName,
          id: categoryId,
          inactive,
          activeTimePeriod,
        } = category;
        const { displayName: categoryDisplayName } = getVoiceProps(
          categoryName,
          persistentVoiceProps
        );
        const currentItem = menuRes.menuItems[menuItemId];
        const { name } = currentItem;
        const { available, unavailableUntil } = getUnAvailableUntilAndAvailable(
          { item: currentItem, stage, unavailableItems }
        );
        const { displayName: menuItemDisplayName, synonyms } = getVoiceProps(
          name,
          persistentVoiceProps
        );

        const menuItemIdToString = String(menuItemId);

        if (
          !Object.keys(acc).includes(menuItemIdToString) ||
          acc[menuItemIdToString].inactive
        ) {
          // Only push the item if it doesn't exit or the current item's category is inactive
          acc[menuItemIdToString] = {
            category: ExpandName(
              categoryDisplayName ? categoryDisplayName : categoryName
            ),
            categoryId: categoryId,
            id: menuItemIdToString,
            name: ExpandName(menuItemDisplayName ? menuItemDisplayName : name),
            synonyms: synonyms || '',
            available: available,
            unavailableUntil: unavailableUntil,
            activeTimePeriod,
            inactive,
          };
        }
      });
    if (category.name === '__omnimod__') {
      const [parsedModSymbolMapping, parsedCodeNameMapping] =
        generateModSymbolMapping(category, menuRes);
      modSymbolMapping = parsedModSymbolMapping;
      codeNameMapping = parsedCodeNameMapping;
    }
    return acc;
  }, {} as GenericMap<TopLevelMenuItem>);

  return { topLevelMenuItems, modSymbolMapping, codeNameMapping };
}

function getMenuOverrides(menuItem: ParsedMenuItem, modality: ModalityType) {
  const isCategoryLevelItem = !!menuItem.categoryId;
  const isModifierLevelItem = menuItem.category === 'modifier';
  let overrides: any[] = [];
  if (menuItem.overrides) {
    overrides = Object.values(menuItem.overrides).filter(
      (o) => o.modalityType === modality
    );
  }
  if (isCategoryLevelItem) {
    overrides = overrides.filter(
      (o) =>
        o.secondaryType === SecondaryType.Category || o.secondaryType === null
    );
  } else if (isModifierLevelItem) {
    overrides = overrides.filter(
      (o) =>
        o.secondaryType === SecondaryType.ModifierGroup ||
        o.secondaryType === null
    );
  }

  const secondaryTypeOverride = overrides.filter(
    (o) => o.secondaryType !== null
  );
  const modalityTypeOverride = overrides.filter(
    (o) => o.secondaryType === null
  );
  return {
    secondaryTypeOverride,
    modalityTypeOverride,
  };
}

export type PRPRestaurantSettings =
  RestaurantInfoQuery['restaurant']['restaurantSettings'];

export function getDefaultTaxRateForModality(
  modality: ModalityType,
  restaurantSettings?: RestaurantSettings
) {
  if (!restaurantSettings) {
    return 0;
  }
  switch (modality) {
    case ModalityType.Togo:
      return restaurantSettings.toGoModalityTaxRate;
    case ModalityType.Delivery:
      return restaurantSettings.deliveryModalityTaxRate;
    case ModalityType.Dinein:
    default:
      return restaurantSettings.dineInModalityTaxRate;
  }
}

export function getMenuItemTax(
  menuItem: ParsedMenuItem,
  modality: ModalityType,
  restaurantSettings?: RestaurantSettings
): number {
  let actualModality = modality;
  const { modalityTypeOverride } = getMenuOverrides(menuItem, actualModality);
  const taxOverride = modalityTypeOverride.find(
    (o) => o.overrideKey === OverrideKey.Tax
  );
  if (taxOverride !== undefined) {
    return parseFloat(taxOverride.overrideValue);
  } else if (modality === ModalityType.Dinein && menuItem.tax != null) {
    return menuItem.tax;
  } else {
    return getDefaultTaxRateForModality(modality, restaurantSettings);
  }
}

export function getMenuItemPrice(
  menuItem: ParsedMenuItem,
  modality?: ModalityType,
  quantity?: number
) {
  let actualModality = modality || ModalityType.Dinein;
  const { modalityTypeOverride, secondaryTypeOverride } = getMenuOverrides(
    menuItem,
    actualModality
  );
  const priceOverride =
    secondaryTypeOverride.find((o) => o.overrideKey === OverrideKey.Price) ||
    modalityTypeOverride.find((o) => o.overrideKey === OverrideKey.Price);
  const price =
    priceOverride === undefined
      ? menuItem.price
      : Math.round(parseFloat(priceOverride.overrideValue) * 100);
  return price * (quantity || 1);
}

export function updateCartItemModality(
  cartItem: CartItem,
  modality: ModalityType
) {
  if (cartItem.modality === modality) {
    return null;
  }
  cartItem.modality = modality;
  Object.keys(cartItem.childModifierGroups).forEach((modGroupId) => {
    const modGroup = cartItem.childModifierGroups[modGroupId];
    Object.keys(modGroup.selectedItems).forEach((cartItemId) => {
      const updatedCartItem = updateCartItemModality(
        modGroup.selectedItems[cartItemId],
        modality
      );
      if (updatedCartItem) {
        modGroup.selectedItems[cartItemId] = updatedCartItem;
      }
    });
  });
  return cartItem;
}

export function updateCartPrices(
  cartItem: CartItem,
  menuItems: GenericMap<ParsedMenuItem>,
  modality: ModalityType
): CartItem | null {
  let updated = false;
  let updatedCartItem = cartItem;
  const menuItem = menuItems[cartItem.itemId];
  if (menuItem) {
    const cartItemPrice = getMenuItemPrice(cartItem, cartItem.modality);
    const menuItemPrice = getMenuItemPrice(menuItem, modality);
    if (menuItemPrice !== cartItemPrice) {
      updatedCartItem = produce(updatedCartItem, (cartItem) => {
        cartItem.price = menuItemPrice;
      });
      updated = true;
    }
    for (let cartModGroup of Object.values(cartItem.childModifierGroups)) {
      const modGroup =
        menuItem.modifierGroups[cartModGroup.menuModifierGroupId];
      for (let item of Object.values(cartModGroup.selectedItems)) {
        const updatedItem = updateCartPrices(
          item,
          modGroup.menuItems,
          modality
        );
        if (updatedItem) {
          updatedCartItem = produce(updatedCartItem, (cartItem) => {
            cartItem.childModifierGroups[
              cartModGroup.menuModifierGroupId
            ].selectedItems[updatedItem.itemId] = updatedItem;
          });
          updated = true;
        }
      }
    }
  }
  return updated ? updatedCartItem : null;
}

export function checkForRequiredAndNotFreeModifier(
  item: ParsedMenuItem
): boolean {
  let result = false;
  firstLevelLoop: for (let modifierGroup of Object.values(
    item.modifierGroups
  )) {
    if (modifierGroup.minimumSelections > 0) {
      for (let child of Object.values(modifierGroup.menuItems)) {
        if (child.price > 0) {
          // if a priced modifier is found, no need to check the rest of modifiers
          result = true;
          break firstLevelLoop;
        }
      }
    }
  }
  return result;
}

export function findTopLevelCartItem(
  wantedCartItem: CartItem,
  cartItems: { [cartId: string]: CartItem },
  topLevelItem?: CartItem
): CartItem | null {
  for (let cartItem of Object.values(cartItems)) {
    if (cartItem.cartItemId === wantedCartItem.cartItemId) {
      if (topLevelItem) {
        return topLevelItem;
      }
      return cartItem;
    }
    const modGroups = Object.values(cartItem.childModifierGroups);
    for (let modGroup of modGroups) {
      const found = findTopLevelCartItem(
        wantedCartItem,
        modGroup.selectedItems,
        topLevelItem ? topLevelItem : cartItem
      );
      if (found) {
        return found;
      }
    }
  }
  return null;
}

// Takes in menu items flattened as we get from webservice instead of in the nested mod group form
export function checkForNoLongerAvailableItems(
  cartItems: GenericMap<CartItem>,
  parsedMenuItems: GenericMap<ParsedMenuItem>
): CartItem[] {
  const unavailableItems: CartItem[] = [];
  for (let cartItem of Object.values(cartItems)) {
    const menuItem = parsedMenuItems[cartItem.itemId];
    if (!menuItem) {
      unavailableItems.push(cartItem);
      continue;
    }

    for (let modGroup of Object.values(cartItem.childModifierGroups)) {
      unavailableItems.push(
        ...checkForNoLongerAvailableItems(
          modGroup.selectedItems,
          parsedMenuItems
        )
      );
    }
  }
  return unavailableItems;
}

type ActiveTimePeriod = {
  alwaysEnabled: boolean;
  day: string;
  start?: string;
  end?: string;
};

export function considerTimePeriodCategory(
  categoryMap: ParsedCategory[],
  timezone: string
): ParsedCategory[] {
  return categoryMap.map((category) => ({
    ...category,
    ...getTimePeriod(category.timePeriods, timezone),
  }));
}

export const getTimePeriod = (timePeriods: TimePeriod[], timezone: string) => {
  const currentUTCTime = moment.utc().format();

  //use the timezone of the restaurant to create the moment objects
  moment.tz.setDefault(timezone);
  let current = moment
    .utc(currentUTCTime, 'YYYY-MM-DD HH-mm-ss Z')
    .tz(timezone);

  const day = current.weekday();

  const timePeriodsLength = timePeriods.length;
  for (let i = 0; i < timePeriodsLength; i++) {
    const { availability, startDate, endDate } = timePeriods[i];
    const availabilityOfCurrent = availability.find((obj) => obj.day === day);

    const isDateSpecific = Boolean(startDate);

    const isWithinDates =
      !isDateSpecific ||
      (current.isSameOrAfter(startDate, 'date') &&
        (!endDate || current.isSameOrBefore(endDate, 'date')));

    if (isWithinDates && availabilityOfCurrent?.alwaysEnabled) {
      return {
        activeTimePeriod: {
          alwaysEnabled: true,
          day: current.format(FULL_WEEKDAY_FORMAT),
        },
        inactive: false,
      };
    }

    if (
      isWithinDates &&
      availabilityOfCurrent?.hours &&
      availabilityOfCurrent.hours.length > 0
    ) {
      const timeRangeLength = availabilityOfCurrent?.hours.length;
      for (let j = 0; j < timeRangeLength; j++) {
        const timeRange = availabilityOfCurrent?.hours[j];
        const updatedTimeRange = timeRange.map((time) => {
          let timeStr = String(time);
          while (timeStr.length < 4) {
            timeStr = '0' + timeStr;
          }
          return timeStr;
        });

        const startTime = current
          .clone()
          .set('hour', Number(updatedTimeRange[0].slice(0, 2)))
          .set('minute', Number(updatedTimeRange[0].slice(2)));

        const endTime = current
          .clone()
          .set('hour', Number(updatedTimeRange[1].slice(0, 2)))
          .set('minute', Number(updatedTimeRange[1].slice(2)));

        const activeTimePeriod = {
          alwaysEnabled: false,
          day: current.format(FULL_WEEKDAY_FORMAT),
          start: startTime.format('h:mma'),
          end: endTime.format('h:mma'),
        };
        if (
          current.isSameOrBefore(endTime) &&
          current.isSameOrAfter(startTime)
        ) {
          return {
            activeTimePeriod,
            inactive: false,
          };
        }

        if (i === timePeriodsLength - 1 && j === timeRangeLength - 1) {
          // All the time period is not active and return the last time range of the last time period to display in the Omnibar search results.
          return {
            activeTimePeriod,
            inactive: true,
          };
        }
      }
    }
  }

  // Always unavailable time period will have array of availability object as {"day": 0, "hours": [], "alwaysEnabled": false} so it will not have activeTimePeriod
  return { inactive: true };
};

export function checkForUnavailableRequiredModifierGroup(
  modifierGroups: GenericMap<ParsedModifierGroup>
) {
  return !!Object.values(modifierGroups).find((modGroup) => {
    if (modGroup.minimumSelections > 0) {
      //check the modifier groups recursively
      const availableModsCounter = Object.values(modGroup.menuItems).filter(
        (child) =>
          child.available &&
          !checkForUnavailableRequiredModifierGroup(
            getOptionModifierGroups(child)
          )
      ).length;
      if (availableModsCounter < modGroup.minimumSelections) {
        return true;
      }
    }
  });
}

export function checkForQuantityExceededItems(
  cartItems: GenericMap<CartItem>,
  parsedMenuItems: GenericMap<ParsedMenuItem>
): CartItem[] {
  const quantityExceededItems: CartItem[] = [];
  let quantityMapping: { [itemId: string]: number } = {};
  for (let cartItem of Object.values(cartItems)) {
    const itemId = cartItem.itemId;
    if (itemId in quantityMapping) {
      quantityMapping[itemId] += 1;
    } else {
      quantityMapping[itemId] = 1;
    }
  }
  for (let cartItem of Object.values(cartItems)) {
    const menuItem = parsedMenuItems[cartItem.itemId];
    if (
      menuItem &&
      menuItem.availableLimitedQuantity &&
      menuItem.availableLimitedQuantity < quantityMapping[menuItem.itemId]
    ) {
      const itemAlreadyExist = quantityExceededItems.find(
        (item) => item.itemId === cartItem.itemId
      );
      if (!itemAlreadyExist) {
        quantityExceededItems.push(cartItem);
      }
      continue;
    }

    for (let modGroup of Object.values(cartItem.childModifierGroups)) {
      quantityExceededItems.push(
        ...checkForQuantityExceededItems(
          modGroup.selectedItems,
          parsedMenuItems
        )
      );
    }
  }
  return quantityExceededItems;
}

export function considerSubCategory(categories: string[]) {
  let subCatMapping: {
    [category: string]: { hasSubCat: boolean; subCats: string[] };
  } = {};
  categories.forEach((cat) => {
    if (cat.indexOf(':') !== -1) {
      //there is a colon in the category name which should be a sub category in BJs menu
      const catName = cat.split(':')[0];
      if (!Object.keys(subCatMapping).includes(catName)) {
        subCatMapping[catName] = { hasSubCat: true, subCats: [] };
      }
      subCatMapping[catName].subCats?.push(cat);
    } else {
      subCatMapping[cat] = { hasSubCat: false, subCats: [] };
    }
  });
  return subCatMapping;
}

export function sortChildrenModGroup(cartItem: CartItem) {
  const modifierGroupsSortOrderMapping = cartItem.sortOrder.reduce(
    (acc: any, o: any) => {
      acc[String(o.id)] = o.sortOrder;
      return acc;
    },
    {} as { [id: string]: any }
  );

  return Object.values(cartItem.childModifierGroups).sort((a: any, b: any) => {
    if (
      modifierGroupsSortOrderMapping[a.menuModifierGroupId] !== null &&
      modifierGroupsSortOrderMapping[b.menuModifierGroupId] !== null
    ) {
      return (
        modifierGroupsSortOrderMapping[a.menuModifierGroupId] -
        modifierGroupsSortOrderMapping[b.menuModifierGroupId]
      );
    } else if (modifierGroupsSortOrderMapping[a.menuModifierGroupId] !== null) {
      return -1;
    } else if (modifierGroupsSortOrderMapping[b.menuModifierGroupId] !== null) {
      return 1;
    }
    return 0;
  });
}

export function sortModGroups(cartItem: CartItem | ParsedMenuItem) {
  const modifierGroupsSortOrderMapping = cartItem.sortOrder.reduce(
    (acc: any, o: any) => {
      acc[String(o.id)] = o.sortOrder;
      return acc;
    },
    {} as { [id: string]: any }
  );

  return Object.values(getOptionModifierGroups(cartItem)).sort(
    (a: any, b: any) => {
      if (
        modifierGroupsSortOrderMapping[a.id] !== null &&
        modifierGroupsSortOrderMapping[b.id] !== null
      ) {
        return (
          modifierGroupsSortOrderMapping[a.id] -
          modifierGroupsSortOrderMapping[b.id]
        );
      } else if (
        modifierGroupsSortOrderMapping[a.menuModifierGroupId] !== null
      ) {
        return -1;
      } else if (
        modifierGroupsSortOrderMapping[b.menuModifierGroupId] !== null
      ) {
        return 1;
      }
      return 0;
    }
  );
}

export type PersistentMenuProperty = {
  property_id: number;
  restaurant_code: string;
  unique_identifier: string;
  property_type: string;
  property_key: string;
  property_value: any;
};

export interface IMenuVersionsResponse {
  id: string;
  restaurant_code: string;
  creator_username: string;
  creator_first_name: string;
  creator_last_name: string;
  commit_id: string;
  updated_at: string;
  created_at: string;
  stage: MenuStages;
  is_active: boolean;
  publisher_username: string;
  publisher_first_name: string;
  publisher_last_name: string;
  commit_created_at: string;
  comment: string;
}

export interface IMenuVersion {
  id: string;
  creatorUsername: string;
  creatorName: string;
  publisherUsername: string;
  publisherName: string;
  commitId: string;
  updatedAt: string;
  createdAt: string;
  comment: string;
  menuCommitUrl?: string;
  stage?: MenuStages;
  isActive: boolean;
}

enum PropertyValueKey {
  displayName = 'display_name',
  description = 'description',
  synonyms = '__synonym__',
  isUpsell = 'is_upsell',
  isUpsellPrompt = 'is_upsell_prompt',
  label = 'label',
}

export function getVoiceProps(
  uniqueIdentifier: string,
  persistentVoiceProps: GenericMap<PersistentMenuProperty>
) {
  const voiceProps: Record<any, any> = {};
  if (uniqueIdentifier in persistentVoiceProps) {
    try {
      const persistentVoiceProp = JSON.parse(
        persistentVoiceProps[uniqueIdentifier].property_value
      );

      for (let key in PropertyValueKey) {
        const propertyValueKeyValue =
          PropertyValueKey[key as keyof typeof PropertyValueKey];
        if (persistentVoiceProp[propertyValueKeyValue] !== undefined) {
          voiceProps[key as keyof typeof PropertyValueKey] =
            persistentVoiceProp[propertyValueKeyValue];
        }
      }
    } catch (err) {
      logger.warn({
        message: 'Parse voice props failed',
        error: err,
      });
    }
  }
  return voiceProps;
}

export function convertModGroupToMap<T extends { menuModifierGroupId: string }>(
  entries: T[]
): GenericMap<T> {
  return entries.reduce((acc, entry) => {
    acc[entry.menuModifierGroupId] = entry;
    return acc;
  }, {} as GenericMap<T>);
}

export function isItem86edIndefinitely(
  available: boolean,
  unavailableUntil?: string
) {
  return !available && !unavailableUntil;
}

/**
 * TODO: Ideally we should add a boolean flag something like
 * "isUnavailableUntilTomorrow" to menu-item object.
 * See JIRA ticket: https://presto.atlassian.net/browse/PRV-4173
 */

/** Checks if a menu-item is 86ed for today */
export const isItem86edToday = memoize(
  (item: MenuItem | ParsedMenuItem | TopLevelMenuItem) => {
    if (item.available || !item.unavailableUntil) return false;

    const unavailableUntil = moment(item.unavailableUntil);

    return unavailableUntil.isAfter(new Date());
  },
  (item) => `${item.available},${item.unavailableUntil}`
);

/** Checks if a menu-item is unavailable due to 86ed or inactive meal period */
export const isUnavailableItem = (
  item: MenuItem | ParsedMenuItem | TopLevelMenuItem
) => isItem86edToday(item) || ((item as any).inactive ?? false);

/**
 * Splits a path string by '__' delimiter while preserving trailing underscores.
 * When encountering '___' (three underscores), it preserves the trailing '_'
 * on the previous segment instead of creating an empty segment.
 *
 * Example: "cheese___mod_codes_sub" splits to ["cheese_", "mod_codes_sub"]
 */
export const splitPathToItem = (pathToItem: string): string[] => {
  const parts = pathToItem.split('__');
  const result: string[] = [];

  for (let i = 0; i < parts.length; i++) {
    const currentPart = parts[i];

    // If current part starts with '_', it means the previous segment
    // should have ended with '_' (from a '___' sequence)
    if (currentPart.startsWith('_') && result.length > 0) {
      // Append '_' to the previous segment
      result[result.length - 1] += '_';
      // Remove the leading '_' from current part
      result.push(currentPart.substring(1));
    } else {
      result.push(currentPart);
    }
  }

  return result;
};

export const checkItemInCartItemTree = ({
  cartItem,
  pathToItem,
  fromSelectModifier,
}: {
  cartItem: CartItem;
  pathToItem: string;
  fromSelectModifier?: boolean;
}): any => {
  const ids = splitPathToItem(pathToItem);
  let splicedId = '';
  if (fromSelectModifier) {
    splicedId = ids.splice(-1, 1)[0] || '';
  }
  let currentItem: any = null;
  currentItem = ids.reduce((item: any, id: string) => {
    if (Object.values(item?.childModifierGroups || {}).length) {
      return item.childModifierGroups[id];
    } else if (Object.values(item?.selectedItems || {}).length) {
      return item.selectedItems[id];
    }
    return null;
  }, cartItem as any);
  if (fromSelectModifier && currentItem?.selectedItems) {
    return currentItem.selectedItems[splicedId];
  }

  return currentItem;
};

/**
 * Finds a menu item or modifier group within a cart item's structure using a path string.
 *
 * @param {Object} params - The parameters for the function.
 * @param {CartItem} params.cartItem - The cart item to search within.
 * @param {string} params.pathToItem - A string representing the path to the desired item, with IDs separated by '__'.
 * @returns {any} The found menu item, modifier group, or null if not found.
 */
export const findMenuItemOrModGroupUsingPathToItem = ({
  cartItem,
  pathToItem,
}: {
  cartItem: CartItem;
  pathToItem: string;
}): any => {
  const ids = splitPathToItem(pathToItem);
  let currentItem: any = null;
  currentItem = ids.reduce((item: any, id: string) => {
    // HITL-1748: an option's nested groups may not be built yet.
    const modifierGroups = item?.modifierGroups
      ? getOptionModifierGroups(item)
      : undefined;
    if (modifierGroups && Object.keys(modifierGroups).includes(id)) {
      return modifierGroups[id];
    } else if (item?.menuItems && Object.keys(item?.menuItems).includes(id)) {
      return item.menuItems[id];
    }
    return null;
  }, cartItem as any);
  return currentItem;
};

//If the current active version matches with the cached menu data version, return the menu data instead of making further API calls
export function isMenuInCache({
  restaurantCode,
  activeCommitId,
  cachedMenu: { menusById, menusOrder },
}: {
  restaurantCode: string;
  activeCommitId: string;
  cachedMenu: {
    menusById: GenericMap<IMenuData>;
    menusOrder: Array<IMenusOrderData>;
  };
}) {
  let storedMenuDetails = {
    menuJSON: {},
    modifierGroupJSON: {},
    categoryAndTimePeriodJSON: {},
    persistentMenuProperty: {},
  };

  const cachedRestaurantIndex = menusOrder.findIndex(
    ({ restaurantId }) => restaurantId === restaurantCode
  );

  if (
    cachedRestaurantIndex > -1 &&
    activeCommitId === menusOrder[cachedRestaurantIndex].menuVersion
  ) {
    storedMenuDetails = menusById[restaurantCode];
    logger.info({
      restaurantCode,
      message:
        'Function isMenuInCache: Cached menu data exists - not calling S3 dynamic menu URL for fetch',
    });
  } else {
    logger.info({
      restaurantCode,
      message:
        'Function isMenuInCache: No cached menu data exists - calling S3 dynamic menu URL for fetch',
    });
  }

  return {
    cachedRestaurantIndex,
    storedMenuDetails,
  };
}

export function isBrandMenuInCache({
  restaurantCode,
  activeCommitId,
  cachedMenu: { brandMenusById, brandMenusOrder },
}: {
  restaurantCode: string;
  activeCommitId: string;
  cachedMenu: {
    brandMenusById: GenericMap<IBrandMenu>;
    brandMenusOrder: Array<IBrandMenusOrderData>;
  };
}) {
  let storedMenuDetails = {} as IBrandMenu;

  const cachedRestaurantIndex = brandMenusOrder.findIndex(
    ({ restaurantId }) => restaurantId === restaurantCode
  );

  if (
    cachedRestaurantIndex > -1 &&
    activeCommitId === brandMenusOrder[cachedRestaurantIndex].menuVersion
  ) {
    storedMenuDetails = brandMenusById[restaurantCode];
    logger.info({
      restaurantCode,
      message:
        'Function isBrandMenuInCache: Cached menu data exists - not calling S3 dynamic menu URL for fetch',
    });
  } else {
    logger.info({
      restaurantCode,
      message:
        'Function isBrandMenuInCache: No cached menu data exists - calling S3 dynamic menu URL for fetch',
    });
  }

  return {
    cachedRestaurantIndex,
    storedMenuDetails,
  };
}

/**
 * Brand menu V2 cache probe — nested brand → version lookup, shared across a
 * brand's restaurants on the same version. Returns the stored entry only when
 * that exact (brand, version) is cached; otherwise `storedEntry` is undefined
 * and the caller downloads the full menu.
 */
export function isBrandMenuInCacheByKey({
  brandKey,
  activeCommitId,
  cachedMenu: { brandMenuCache },
}: {
  brandKey: string;
  activeCommitId: string;
  cachedMenu: {
    brandMenuCache: GenericMap<GenericMap<IBrandMenuCacheEntry>>;
  };
}): { storedEntry?: IBrandMenuCacheEntry } {
  const storedEntry = brandMenuCache[brandKey]?.[activeCommitId];

  if (storedEntry) {
    return { storedEntry };
  }

  return { storedEntry: undefined };
}

export function formatUnavailableItems(
  unavailable_items: IFetchUnavailableItem[]
) {
  let formattedUnavailableItems: Record<string, IUnavailableItem> = {};

  if (unavailable_items?.length) {
    formattedUnavailableItems = unavailable_items.reduce((itemList, item) => {
      itemList[item.item_unique_identifier] = Object.entries(item).reduce(
        (acc: any, [key, value]) => {
          const updatedKey = snakeToCamelCase(key);
          acc[updatedKey] = value;
          return acc;
        },
        {} as IUnavailableItem
      );

      return itemList;
    }, {} as Record<string, IUnavailableItem>);
  }

  return formattedUnavailableItems;
}

export async function fetchMenuBasedOnStage({
  restaurantCode,
  state,
  currentMenuVersion,
  currentStage,
  dispatch,
}: {
  restaurantCode: string;
  state: RootState;
  currentMenuVersion: string;
  currentStage: MenuStages;
  dispatch: Function;
}) {
  let unavailableItems: Record<string, IUnavailableItem> = {};
  const {
    cachedMenu,
    config: { PRP_PERSISTENT_API, PRP_API, MENU_API },
  } = state;

  if (
    currentMenuVersion === 'latest' &&
    currentStage === MenuStages.PLAYGROUND
  ) {
    const client = getGraphQLClient(PRP_API);

    const [
      categoryAndTimePeriodJSON,
      persistentMenuProperty,
      menuJSON,
      modifierGroupJSON,
    ] = await Promise.all([
      client.categoryAndTimePeriodQuery({
        restaurantCode,
        version: null,
      }) as any as Promise<CategoryAndTimePeriodQueryQuery>,
      getPersistentMenuPropByRestaurant(PRP_PERSISTENT_API, restaurantCode, {
        property_key: VOICE_PROPERTIES,
      }),
      client.menuItemsQuery({
        restaurantCode,
        version: null,
      }) as any as Promise<MenuItemsQueryQuery>,
      client.modifierGroupsQuery({
        restaurantCode,
        version: null,
      }) as any as Promise<ModifierGroupsQueryQuery>,
    ]);

    return {
      categoryAndTimePeriodJSON,
      menuJSON,
      modifierGroupJSON,
      persistentMenuProperty,
      unavailableItems,
    };
  } else {
    const params: { [key: string]: string } = {
      restaurant_code: restaurantCode,
    };
    if (currentMenuVersion) {
      params['commit_id'] = currentMenuVersion;
    }

    const { data } = await getMenuURLFromMenuAPI(
      MENU_API,
      currentStage,
      params
    );
    const {
      menu_url,
      unavailable_items,
      commit_id: activeCommitId,
      comment: activeMenuTitle,
    } = data || {};

    if (menu_url) {
      unavailableItems = formatUnavailableItems(unavailable_items);

      const { cachedRestaurantIndex, storedMenuDetails } = isMenuInCache({
        restaurantCode,
        activeCommitId,
        cachedMenu,
      });

      if (Object.keys(storedMenuDetails.menuJSON).length) {
        return { ...storedMenuDetails, unavailableItems };
      } else {
        const {
          categories,
          timePeriods,
          menuItemSettings,
          menuItems,
          menuOverrides,
          posProperties,
          modifierGroups,
          voiceProperties,
        }: any = await getMenuFromMenuAPI(menu_url, {});

        const outputResponse = {
          categoryAndTimePeriodJSON: {
            categories,
            timePeriods,
          },
          menuJSON: {
            menuItemSettings,
            menuItems,
            menuOverrides,
            posProperties,
          },
          modifierGroupJSON: {
            modifierGroups,
          },
          persistentMenuProperty: voiceProperties,
          unavailableItems,
        };

        if (cachedRestaurantIndex > -1) {
          //For a specific restaurant, if the active version is different from the one which is already cached
          dispatch(
            updateMenuData({
              restaurantId: restaurantCode,
              menuVersion: activeCommitId,
              menuData: outputResponse,
              index: cachedRestaurantIndex,
              menuTitle: activeMenuTitle || '',
            })
          );
        } else {
          //Cache the menu data for quick access to menu data in the future
          dispatch(
            addMenuData({
              restaurantId: restaurantCode,
              menuVersion: activeCommitId,
              menuData: outputResponse,
              menuTitle: activeMenuTitle || '',
            })
          );
        }
        return outputResponse;
      }
    }

    return {
      menuJSON: {},
      modifierGroupJSON: {},
      categoryAndTimePeriodJSON: {},
      persistentMenuProperty: {},
      unavailableItems,
    };
  }
}

export const generateMenuTitle = (commitId: string, comment?: string) =>
  comment ? `${commitId} - ${comment}` : commitId;

export const getUnAvailableUntilAndAvailable = ({
  item,
  stage,
  unavailableItems,
}: {
  item: MenuItem;
  stage: string;
  unavailableItems: Record<string, IUnavailableItem>;
}) => {
  let { unavailableUntil, name = '', available } = item || {};
  if (stage.toUpperCase() === MenuStages.LIVE && unavailableItems[name]) {
    // Unavailable today item will have unavailableUntil with timestamp when the item becomes available in UTC format i.e. “2024-11-08T18:30:00Z”;
    // Unavailable indefinitely item will have null as the value of unavailableUntil;
    unavailableUntil = unavailableItems[name].unavailableUntil;
    if (unavailableUntil) {
      // Item is marked as unavailable temporarily and needs to check the timestamp with current timestamp to see whether it should be unavailable or not
      const unavailableNow = moment(
        unavailableItems[name].unavailableUntil
      ).isAfter(new Date()); // Check if unavailableUntil is still valid
      available = !unavailableNow && available;
    } else {
      // Item is marked as unavailable indefinitely, so it should be unavailable
      available = false;
    }
  }
  return { unavailableUntil, available };
};

export const getComboPrpIdToALaCartePrpId = (
  autoComboPrpIds: string[],
  menuRes: MenuResponses
) => {
  const autoComboModifierPrpIds: string[] = [];
  if (autoComboPrpIds) {
    autoComboPrpIds.forEach((autoComboPrpId) => {
      let autoCombo = menuRes.menuItems[autoComboPrpId];
      const posProperties = getPosProperties(autoCombo, menuRes);
      const isDummyItem =
        Object.values(posProperties).find((posProp) => posProp.key === 'pos_id')
          ?.value === DUMMY_ITEM;
      if (isDummyItem && autoCombo.modifierGroups.length) {
        menuRes.modifierGroups[autoCombo.modifierGroups[0]].menuItems.forEach(
          (id) => {
            menuRes.menuItems[id].modifierGroups.forEach((modifierGroupId) => {
              autoComboModifierPrpIds.push(
                ...menuRes.modifierGroups[modifierGroupId]?.menuItems.map(
                  (id) => String(id)
                )
              );
            });
          }
        );
      } else {
        autoCombo.modifierGroups.forEach((modifierGroupId) => {
          autoComboModifierPrpIds.push(
            ...menuRes.modifierGroups[modifierGroupId]?.menuItems.map((id) =>
              String(id)
            )
          );
        });
      }
    });
  }

  const posIdOrNameToPrpId: { [key: string]: string } = {};
  Object.values(menuRes.menuItems).forEach((menuItem) => {
    const posProperties = getPosProperties(menuItem, menuRes);
    const posId = Object.values(posProperties).find(
      (posProp) => posProp.key === 'pos_id'
    )?.value;
    const posName = Object.values(posProperties).find(
      (posProp) => posProp.key === 'pos_name'
    )?.value;
    if (posId) posIdOrNameToPrpId[posId] = menuItem.id;
    if (posName) posIdOrNameToPrpId[posName] = menuItem.id;
  });

  const aLaCartePosIdToPrpId: { [key: string]: string } = {};
  const comboPrpIdToALaCartePrpId: { [key: string]: string } = {};

  autoComboModifierPrpIds.forEach((comboPrpId) => {
    const posProperties = getPosProperties(
      menuRes.menuItems[comboPrpId],
      menuRes
    );
    const aLaCartePosId = Object.values(posProperties).find(
      (posProp) => posProp.key === 'a_la_carte_pos_id'
    )?.value;
    if (aLaCartePosId) {
      aLaCartePosIdToPrpId[aLaCartePosId] = comboPrpId;
      if (aLaCartePosId in posIdOrNameToPrpId)
        comboPrpIdToALaCartePrpId[comboPrpId] =
          posIdOrNameToPrpId[aLaCartePosId];
    } else {
      comboPrpIdToALaCartePrpId[comboPrpId] = comboPrpId;
    }
  });

  return comboPrpIdToALaCartePrpId;
};

export const findCartItemInMenuGraphByName = (
  cartItem: CartItem,
  menuItem: ParsedMenuItem
) => {
  let menuItemMatchByNameId = '';
  let modGroupMatchByNameId = '';
  Object.values(menuItem.modifierGroups).forEach((modGroup) => {
    Object.values(modGroup.menuItems).forEach((menuItem) => {
      if (menuItem.name === cartItem.name) {
        menuItemMatchByNameId = menuItem.id;
        modGroupMatchByNameId = modGroup.id;
      }
    });
  });
  return { menuItemMatchByNameId, modGroupMatchByNameId };
};

export const getEligibleAutoComboItem = (
  autoCombo: MenuItem,
  menuRes: MenuResponses,
  autoComboPrpId: string,
  cartItemsQuantity: Record<string, number>,
  cartItems: Record<string, CartItem>
) => {
  const comboItemMatches: IComboItemMatch[] = [];
  let isAutoComboEligible = false;
  const posProperties = getPosProperties(autoCombo, menuRes);
  const isDummyItem =
    Object.values(posProperties).find((posProp) => posProp.key === 'pos_id')
      ?.value === DUMMY_ITEM;
  let nonDummyItemMatch;
  try {
    if (isDummyItem) {
      //find the non-dummy combo items
      const realComboItems =
        menuRes.modifierGroups[autoCombo.modifierGroups[0]].menuItems;
      for (let i = 0; i < realComboItems.length; i++) {
        const cartCopy = Object.assign({}, cartItems);
        const cartItemsQuantityCopy = Object.assign({}, cartItemsQuantity);
        const nonDummyItem = menuRes.menuItems[realComboItems[i]];
        isAutoComboEligible = nonDummyItem.modifierGroups.every(
          (modifierGroupId) => {
            const modifiersPrpIds = menuRes.modifierGroups[
              modifierGroupId
            ].menuItems.map((id) => String(id));

            return modifiersPrpIds.find((modifierPrpId) => {
              const modifier = menuRes.menuItems[modifierPrpId];
              const modifierPosProperties = getPosProperties(modifier, menuRes);

              const match = getCartItemMatchByPosProperty(
                modifierPosProperties,
                cartCopy
              );
              if (match) {
                const cartItemId = match.cartItemId;
                comboItemMatches.push({
                  autoComboPrpId,
                  nonDummyItemPrpId: nonDummyItem.id,
                  modifierGroupId: String(modifierGroupId),
                  modifierPrpId,
                  cartItemId,
                });
                if (cartItemsQuantityCopy[cartItemId] > 1) {
                  cartItemsQuantityCopy[cartItemId] -= 1;
                } else {
                  delete cartCopy[cartItemId];
                  delete cartItemsQuantityCopy[cartItemId];
                }
              }
              return match;
            });
          }
        );

        if (isAutoComboEligible) {
          nonDummyItemMatch = nonDummyItem;
          break;
        } else {
          comboItemMatches.length = 0; // Reset if no eligible auto combo found
        }
      }
    } else {
      isAutoComboEligible = autoCombo.modifierGroups.every(
        (modifierGroupId) => {
          const modifiersPrpIds = menuRes.modifierGroups[
            modifierGroupId
          ].menuItems.map((id) => String(id));
          return modifiersPrpIds.find((modifierPrpId) => {
            // find cart item match
            const modifier = menuRes.menuItems[modifierPrpId];
            const modifierPosProperties = getPosProperties(modifier, menuRes);

            const match = getCartItemMatchByPosProperty(
              modifierPosProperties,
              cartItems
            );
            if (match) {
              const cartItemId = match.cartItemId;
              comboItemMatches.push({
                autoComboPrpId,
                modifierGroupId: String(modifierGroupId),
                modifierPrpId,
                cartItemId,
              });
              if (cartItemsQuantity[cartItemId] > 1) {
                cartItemsQuantity[cartItemId] -= 1;
              } else {
                delete cartItems[cartItemId];
                delete cartItemsQuantity[cartItemId];
              }
            }
            return match;
          });
        }
      );
    }
  } catch (error) {
    logger.error({
      message: 'Get eligible Auto Combo Failed',
      error: JSON.stringify(error),
    });
  }
  return {
    isAutoComboEligible,
    comboItemMatches,
    isDummyItem,
    nonDummyItemMatch,
  };
};

const isALaCarteItemMatch = (
  menuItemPosProperties: Record<string, PosProperties>,
  cartItemPosProperties: Record<string, PosProperties>
) => {
  const menuItemPosId =
    getPosPropertyValue(menuItemPosProperties, A_LA_CARTE_POS_ID) ||
    getPosPropertyValue(menuItemPosProperties, POS_ID);
  const menuItemPosName =
    getPosPropertyValue(menuItemPosProperties, A_LA_CARTE_POS_ID) ||
    getPosPropertyValue(menuItemPosProperties, POS_NAME);

  const cartItemPosId = getPosPropertyValue(cartItemPosProperties, POS_ID);
  const cartItemPosName = getPosPropertyValue(cartItemPosProperties, POS_NAME);

  if (
    menuItemPosId &&
    menuItemPosId !== DUMMY_ITEM &&
    menuItemPosId === cartItemPosId
  )
    return true;
  return !!(menuItemPosName && menuItemPosName === cartItemPosName);
};

const getCartItemMatchByPosProperty = (
  menuItemPosProperties: Record<string, PosProperties>,
  cartItems: Record<string, CartItem>
) => {
  return Object.values(cartItems).find((cartItem) => {
    const node = getCartItemNonDummyNode(cartItem);

    return isALaCarteItemMatch(menuItemPosProperties, node.posProperties);
  });
};

export const fetchBrandMenu = async ({
  restaurantCode,
  state,
  dispatch,
}: {
  restaurantCode: string;
  state: RootState;
  dispatch: Function;
}) => {
  const {
    cachedMenu,
    config: { MU_MENU_API, MU_MENU_API_TOKEN },
  } = state;

  const url =
    MU_MENU_API + `/menu/restaurants/${restaurantCode}?include_brand_menu=true`;

  const {
    menu: {
      brand_menu: brandMenuJson,
      utterances_url: utterancesURL,
      version: brandMenuVersion,
    },
    restaurant_unavailable_entities: restaurantUnavailableEntities = [],
    restaurant_meal_periods: restaurantMealPeriods = [],
    restaurant_code: primaryRestaurantCode,
  }: {
    menu: {
      brand_menu: IBrandMenu;
      utterances_url: string;
      version: number;
    };
    restaurant_unavailable_entities: IRestaurantUnavailableEntity[];
    restaurant_meal_periods: IRestaurantMealPeriod[];
    restaurant_code?: string;
  } = await getMenuURLFromMenuUnificationMenuAPI(url, MU_MENU_API_TOKEN, {
    // HITL-1800: this response carries the whole brand menu inline, so it gets
    // the loose bound rather than the 12s default for control-plane reads.
    timeoutMs: GATEWAY_TIMEOUT_MS,
  });

  const activeCommitId = brandMenuVersion.toString();

  logger.info({
    restaurantCode,
    message: `Function fetchBrandMenu: Menu received inline — commit id ${activeCommitId}`,
  });

  let brandMenu: IBrandMenu;
  const { cachedRestaurantIndex, storedMenuDetails } = isBrandMenuInCache({
    restaurantCode,
    activeCommitId,
    cachedMenu,
  });

  let utterancesPromise: Promise<IUtterance[] | undefined> | undefined;

  if (Object.keys(storedMenuDetails).length) {
    brandMenu = storedMenuDetails;

    const cachedUtterances = cachedMenu.brandMenuUtterancesById[restaurantCode];
    if (cachedUtterances) {
      logger.info({
        restaurantCode,
        message:
          'Function fetchBrandMenu: Using cached utterances — skipping utterances_url fetch',
      });
      utterancesPromise = Promise.resolve(cachedUtterances);
    }
  } else {
    brandMenu = brandMenuJson;

    if (cachedRestaurantIndex > -1) {
      dispatch(
        updateBrandMenuData({
          restaurantId: restaurantCode,
          menuVersion: activeCommitId,
          brandMenuData: brandMenu,
          index: cachedRestaurantIndex,
        })
      );
    } else {
      dispatch(
        addBrandMenuData({
          restaurantId: restaurantCode,
          menuVersion: activeCommitId,
          brandMenuData: brandMenu,
        })
      );
    }
  }

  if (!utterancesPromise && utterancesURL) {
    utterancesPromise = getMenuFromMenuAPI(utterancesURL, {})
      .then((response) => {
        if (Array.isArray(response)) {
          const utterances = response as IUtterance[];
          dispatch(
            cacheBrandMenuUtterances({
              restaurantId: restaurantCode,
              utterances,
            })
          );
          return utterances;
        }
        logger.warn({
          restaurantCode,
          message:
            'Function fetchBrandMenu: Unexpected utterances response format, expected array',
          moreInfo: {
            utterancesURL,
            responseType: typeof response,
          },
        });
        return undefined;
      })
      .catch((error) => {
        logger.warn({
          restaurantCode,
          message:
            'Function fetchBrandMenu: Error fetching intent utterances from utterances_url',
          moreInfo: {
            utterancesURL,
            error,
          },
        });
        return undefined;
      });
  }

  return {
    brandMenu,
    restaurantUnavailableEntities,
    restaurantMealPeriods,
    restaurantCode: primaryRestaurantCode || restaurantCode,
    utterancesPromise,
  };
};

/**
 * Brand menu V2 — version-first fetch, cached per brand.
 *
 * 1. Cheap metadata probe (NO include_brand_menu) → version + per-restaurant
 *    dynamic data, without the heavy brand_menu payload.
 * 2. Probe the brand cache by `brandKey` + version.
 * 3. HIT  → serve the cached brand menu; the heavy download is skipped.
 *    MISS → re-request WITH include_brand_menu=true and cache under the brand.
 *
 * The dynamic per-restaurant data (unavailable entities, meal periods) is
 * ALWAYS taken fresh from step 1 and never cached. Return shape mirrors
 * `fetchBrandMenu` so the thunk consumer is unchanged.
 */
export const fetchBrandMenuV2 = async ({
  restaurantCode,
  brandKey,
  state,
  dispatch,
}: {
  restaurantCode: string;
  brandKey: string;
  state: RootState;
  dispatch: Function;
}) => {
  const {
    cachedMenu,
    config: { MU_MENU_API, MU_MENU_API_TOKEN },
  } = state;

  const metadataUrl = MU_MENU_API + `/menu/restaurants/${restaurantCode}`;

  const {
    brand_menu: { utterances_url: utterancesURL, version: brandMenuVersion },
    restaurant_unavailable_entities: restaurantUnavailableEntities = [],
    restaurant_meal_periods: restaurantMealPeriods = [],
    restaurant_code: primaryRestaurantCode,
  }: {
    brand_menu: {
      utterances_url: string;
      version: number;
    };
    restaurant_unavailable_entities: IRestaurantUnavailableEntity[];
    restaurant_meal_periods: IRestaurantMealPeriod[];
    restaurant_code?: string;
  } = await getMenuURLFromMenuUnificationMenuAPI(
    metadataUrl,
    MU_MENU_API_TOKEN
  );

  const activeCommitId = brandMenuVersion.toString();

  // Record this restaurant's current version (per-restaurant — versions can
  // diverge across a brand's stores). Feeds getMenuVersion; written on hit & miss.
  dispatch(
    setBrandMenuVersionForRestaurant({
      restaurantCode,
      version: activeCommitId,
    })
  );

  const { storedEntry } = isBrandMenuInCacheByKey({
    brandKey,
    activeCommitId,
    cachedMenu,
  });

  let brandMenu: IBrandMenu;
  let utterancesPromise: Promise<IUtterance[] | undefined> | undefined;

  if (storedEntry) {
    brandMenu = storedEntry.brandMenu;
    if (storedEntry.utterances) {
      utterancesPromise = Promise.resolve(storedEntry.utterances);
    }
    logger.info({
      restaurantCode,
      brandKey,
      message: `Function fetchBrandMenuV2: brand cache hit (${brandKey}@${activeCommitId}) — heavy download skipped`,
    });
  } else {
    logger.info({
      restaurantCode,
      brandKey,
      message: `Function fetchBrandMenuV2: brand cache miss (${brandKey}@${activeCommitId}) — downloading full brand menu`,
    });

    const fullUrl =
      MU_MENU_API +
      `/menu/restaurants/${restaurantCode}?include_brand_menu=true`;

    const {
      menu: { brand_menu: brandMenuJson },
    }: {
      menu: { brand_menu: IBrandMenu };
    } = await getMenuURLFromMenuUnificationMenuAPI(fullUrl, MU_MENU_API_TOKEN, {
      // HITL-1800: the full brand menu download — see fetchBrandMenu above.
      timeoutMs: GATEWAY_TIMEOUT_MS,
    });

    brandMenu = brandMenuJson;
    dispatch(setBrandMenu({ brandKey, version: activeCommitId, brandMenu }));
  }

  if (!utterancesPromise && utterancesURL) {
    utterancesPromise = getMenuFromMenuAPI(utterancesURL, {})
      .then((response) => {
        if (Array.isArray(response)) {
          const utterances = response as IUtterance[];
          dispatch(
            setBrandMenuUtterances({
              brandKey,
              version: activeCommitId,
              utterances,
            })
          );
          return utterances;
        }
        logger.warn({
          restaurantCode,
          brandKey,
          message:
            'Function fetchBrandMenuV2: Unexpected utterances response format, expected array',
          moreInfo: {
            utterancesURL,
            responseType: typeof response,
          },
        });
        return undefined;
      })
      .catch((error) => {
        logger.warn({
          restaurantCode,
          brandKey,
          message:
            'Function fetchBrandMenuV2: Error fetching intent utterances from utterances_url',
          moreInfo: {
            utterancesURL,
            error,
          },
        });
        return undefined;
      });
  }

  return {
    brandMenu,
    restaurantUnavailableEntities,
    restaurantMealPeriods,
    restaurantCode: primaryRestaurantCode || restaurantCode,
    utterancesPromise,
  };
};

export const getParentModifier = (pathToItem: string) => {
  let parentModifier = '';

  const nodesInPath = splitPathToItem(pathToItem);
  if (nodesInPath.length > 2) {
    parentModifier = nodesInPath[nodesInPath.length - 2];
  }

  return parentModifier;
};

export const isModGroupSingleSelection = (modGroup: any) => {
  return modGroup.min === modGroup.max && modGroup.max === 1;
};

export const isModGroupDuplicateSelection = (modGroup: any) => {
  return modGroup.min === modGroup.max && modGroup.max > 1;
};

export const isDefaultItemInModGroup = (
  id: string,
  modGroup: ParsedModifierGroup
) => {
  if (!modGroup.defaultSelectedItemIds) return false;

  // Handle both string[] and number[] cases
  return modGroup.defaultSelectedItemIds.some((itemId) =>
    typeof itemId === 'string' ? itemId === id : String(itemId) === id
  );
};

/**
 * Get nested modifier groups for a specific selected modifier group
 * This is used for hierarchical navigation - going deeper into a selected modifier group
 */
/**
 * Helper function to construct path for nested modifier groups
 */
function constructNestedModGroupPath(
  selectedModGroupId: string,
  itemId: string,
  modGroupId: string
): string {
  return [selectedModGroupId, itemId, modGroupId].join('__');
}

/**
 * Helper function to get nested modifier groups from selected items
 */
function getNestedModGroupsFromSelectedItems(
  childModGroup: CartModifierGroup,
  selectedModGroupId: string,
  withPaths: true
): Array<{ modGroup: ParsedModifierGroup; pathToItem: string }>;
function getNestedModGroupsFromSelectedItems(
  childModGroup: CartModifierGroup,
  selectedModGroupId: string,
  withPaths: false
): ParsedModifierGroup[];
function getNestedModGroupsFromSelectedItems(
  childModGroup: CartModifierGroup,
  selectedModGroupId: string,
  withPaths: boolean
):
  | Array<{ modGroup: ParsedModifierGroup; pathToItem: string }>
  | ParsedModifierGroup[] {
  const result: Array<{ modGroup: ParsedModifierGroup; pathToItem?: string }> =
    [];

  Object.values(childModGroup.selectedItems).forEach(
    (selectedItem: CartItem) => {
      const itemModGroups = sortModGroups(selectedItem);
      itemModGroups.forEach((modGroup) => {
        if (withPaths) {
          const pathToItem = constructNestedModGroupPath(
            selectedModGroupId,
            selectedItem.id,
            modGroup.id
          );
          result.push({ modGroup, pathToItem });
        } else {
          result.push({ modGroup });
        }
      });
    }
  );

  return withPaths
    ? (result as Array<{ modGroup: ParsedModifierGroup; pathToItem: string }>)
    : result.map((r) => r.modGroup);
}

/**
 * Helper function to get nested modifier groups from menu structure
 */
function getNestedModGroupsFromMenuStructure(
  topLevelModGroup: ParsedModifierGroup,
  selectedModGroupId: string
): Array<{ modGroup: ParsedModifierGroup; pathToItem: string }> {
  const result: Array<{ modGroup: ParsedModifierGroup; pathToItem: string }> =
    [];

  Object.values(topLevelModGroup.menuItems).forEach(
    (menuItem: ParsedMenuItem) => {
      if (
        menuItem.modifierGroups &&
        Object.keys(getOptionModifierGroups(menuItem)).length > 0
      ) {
        const itemModGroups = sortModGroups(menuItem);
        itemModGroups.forEach((modGroup) => {
          const pathToItem = constructNestedModGroupPath(
            selectedModGroupId,
            menuItem.id,
            modGroup.id
          );
          result.push({ modGroup, pathToItem });
        });
      }
    }
  );

  return result;
}

/**
 * Get nested modifier groups for a specific selected modifier group
 * Returns only the modifier groups without paths
 */
export function getNestedModGroupsForSelectedModGroup(
  cartItem: CartItem,
  selectedModGroupId: string
): ParsedModifierGroup[] {
  const childModGroup = cartItem.childModifierGroups[selectedModGroupId];

  if (childModGroup) {
    return getNestedModGroupsFromSelectedItems(
      childModGroup,
      selectedModGroupId,
      false
    );
  }

  return [];
}

/**
 * Get nested modifier groups with their paths for a specific selected modifier group
 * This is used for hierarchical navigation - going deeper into a selected modifier group
 */
export function getNestedModGroupsWithPathsForSelectedModGroup(
  cartItem: CartItem,
  selectedModGroupId: string
): Array<{ modGroup: ParsedModifierGroup; pathToItem: string }> {
  // First, try to find nested modifier groups from already selected items
  const childModGroup = cartItem.childModifierGroups[selectedModGroupId];

  if (childModGroup && Object.keys(childModGroup.selectedItems).length > 0) {
    return getNestedModGroupsFromSelectedItems(
      childModGroup,
      selectedModGroupId,
      true
    );
  }

  // If no items are selected yet, look at the menu structure
  const topLevelModGroup = cartItem.modifierGroups[selectedModGroupId];
  if (topLevelModGroup) {
    return getNestedModGroupsFromMenuStructure(
      topLevelModGroup,
      selectedModGroupId
    );
  }

  return [];
}
